import React from 'react'
import PropTypes from 'prop-types'
import LazyLoad from 'vanilla-lazyload'
import inView from 'in-view'
import { Header, ThemeOverride } from '../core'
import { helpers, prismicHelpers } from '../../helpers'
import TopTenProductCard from './TopTenProductCard'
import DetailedProductCard from './DetailedProductCard'
import HorizontalProductCard from './HorizontalProductCard'
import HorizontalScroller from './HorizontalScroller'

/**
* ProductList component
*
* Displays a list of products. If no products are found then nothing is rendered.
*
* The way products are displayed depends on the `style` that is passed in:
* - `Top 10` is for displaying products in a Top 10 list.
* - `Detailed Cards` is for displaying detailed product cards.
*/
class ProductList extends React.Component {
  constructor(props) {
    super(props)
    this.uid = helpers.generateID('productList')
    this.selector = `#${this.uid}`
    this.containerSelector = (props.data.style && props.data.style === 'vertical') ? `#${this.uid} .product-list-content` : `#${this.uid} .scroller-content`

    this.state = {
      products: props.data.products,
      title: props.data.title,
      linkText: props.data.linkText,
      link: props.data.link,
      listName: props.data.listName,
      loading: true,
      error: false,
    }

    this.getProductData = this.getProductData.bind(this)
    this.trackProductImpression = this.trackProductImpression.bind(this)
    this.replaceImageWithFallback = this.replaceImageWithFallback.bind(this)
    this.renderProducts = this.renderProducts.bind(this)
  }

  /**
  * Fetches product data once the component is in the viewport.
  */
  componentDidMount() {
    inView(this.selector)
      .once('enter', this.getProductData)
  }

  /**
  * Adds lazy loading of product images.
  */
  componentDidUpdate() {
    if (this.state.error || !this.state.products.length) {
      return
    }
    new LazyLoad({
      container: document.querySelector(this.containerSelector),
      elements_selector: `#${this.uid} .is-lazy`,
      threshold: 100,
      data_src: 'original',
      data_srcset: 'original-set',
      callback_set: this.trackProductImpression,
      callback_error: this.replaceImageWithFallback,
    })
  }

  /**
  * `on_set` callback for image lazy load on product cards. Tracks a product
  * impression based on data from the given image.
  *
  * @param image {element} image element with tracking data attached.
  */
  trackProductImpression(image) {
    if (!image || !image.dataset.position) {
      return
    }
    const product = this.state.products[Number(image.dataset.position) - 1]

    if (!product) {
      return
    }

    let price = null


    let sku = null

    // Parse the price of the variant.
    if (product.variant && product.variant.price) {
      price = product.variant.price
    }

    // Parse the sku of the variant.
    if (product.variant && product.variant.sku) {
      sku = product.variant.sku
    } else {
      sku = product.sku || null
    }

    $.track('product impression', {
      id: sku,
      name: product.name,
      list: this.state.listName,
      brand: helpers.formatProducers(product.producers),
      category: product.variant ? product.variant.oracle_department_name : null,
      variant: product.variant ? product.variant.options_text_analytics : null,
      position: image.dataset.position,
      price,
    })
  }

  /**
  * `on_error` callback for image lazy load on product cards. Replaces the given
  * `image` dom element with a fallback text replacement with the `image`'s '
  * `fallbackText` data attribute value.
  *
  * @param image {element} failed image element that needs to be replaced.
  */
  replaceImageWithFallback(image) {
    debug.image('FAILURE', image)
    const fallbackText = document.createElement('span')

    if (!image || !('fallbackText' in image.dataset)) {
      return
    }
    fallbackText.className = `product-fallback ${this.props.data.style === 'top-ten' ? 'has-drop-shadow' : ''}`
    fallbackText.appendChild(document.createTextNode(image.dataset.fallbackText))
    image.replaceWith(fallbackText)
  }

  /**
  * Fetches and formats a list of products from the server.
  * TODO: Someday we might consider abstracting api calls to a separate location.
  */
  getProductData() {
    // Grab a list of just product skus.
    const path = this.props.data.productURL ? this.props.data.productURL : `/api/prismic/product_lists/${this.props.data.id}`
    const query = this.props.data.query

    debug.data('GET', path)
    debug.performanceStart(path)
    $.getJSON(path)
      .done((data) => {
        debug.performanceEnd(path)
        debug.data('SUCCESS', data)

        if (data.status !== 'success' || data.data.products.length < 3) {
          this.setState({
            error: true,
            loading: false,
          })

          return
        }

        const updatedState = {}

        // Standardize variants and variants_including_master
        data.data.products
          .map((product) => {
            if (!product.variants && product.variants_including_master) {
              product.variants = product.variants_including_master
              delete product.variants_including_master
            }

            return product
          })

        // Combine prismic data with product data
        // If this list is dynamic, we can assume there is no data to match up.
        // We can also try to set values that might not have been entered in Prismic.
        if (this.props.isDynamic) {
          if (data.data.title && !this.state.title) {
            updatedState.title = data.data.title
          }

          if (data.data.link && !this.state.link) {
            updatedState.link = data.data.link
          }

          if (data.data.linkText && !this.state.linkText) {
            updatedState.linkText = data.data.linkText
          }

          if (data.data.listName && !this.state.listName) {
            updatedState.listName = data.data.listName
          }

          updatedState.products = data.data.products
        } else {
          // Try to match up the products from Prismic with the products from the database.
          updatedState.products = this.props.data.products
            .map((product) => {
              for (let i = 0; i < data.data.products.length; i++) {
                const currentProduct = data.data.products[i]

                currentProduct.variants = currentProduct.variants || []

                for (let j = 0; j < currentProduct.variants.length; j++) {
                  if (currentProduct.variants[j].sku === product.sku) {
                    return helpers.formatProductJSON(Object.assign({}, currentProduct, product))
                  }
                }
              }
            })
            .filter(product => product !== null && product !== undefined)
        }

        updatedState.loading = false
        this.setState(updatedState)
      })
      .fail((data) => {
        console.error(data)
        this.setState({
          error: true,
          loading: false,
        })
      })
  }

  /**
  * Renders individual Product components.
  */
  renderProducts() {
    if (this.props.data.style === 'top-ten') {
      return this.state.products
        .map((product, idx) => (
          <div className="card-wrapper" key={idx}>
            <TopTenProductCard
              product={product}
              loading={this.state.loading}
              position={idx + 1}
              listName={this.state.listName}
            />
          </div>
        ))
    } else if (this.props.data.style === 'vertical') {
      return this.state.products
        .map((product, idx) => (
          <HorizontalProductCard
            product={product}
            loading={this.state.loading}
            key={idx}
            position={idx + 1}
            listName={this.props.data.uid}
          />
        ))
    }

    return this.state.products
      .map((product, idx) => (
        <div className="card-wrapper" key={idx}>
          <DetailedProductCard
            product={product}
            loading={this.state.loading}
            position={idx + 1}
            listName={this.state.listName}
          />
        </div>
      ))
  }

  render() {
    let context
    const viewableClass = prismicHelpers.formatViewClasses(this.props.data)


    const isHorizontal = this.props.data.style !== 'vertical'


    const backgroundClass = isHorizontal ? 'has-dark-background' : ''

    if (this.props.context) {
      context = document.querySelector(this.props.context)
    }

    if (!this.state.products.length || !prismicHelpers.userHasPermission(this.props.data) || (!this.state.loading && this.state.error)) {
      // Hide context
      if (context) {
        context.classList.add('hidden')
      }

      return false
    }

    // Show context and add visibility classes.
    if (context) {
      context.classList.remove('hidden')
      viewableClass
        .split(' ')
        .map((className) => {
          if (className) context.classList.add(className)
        })
    }

    return (
      <div id={this.uid} className={`product-list ${this.props.data.style || 'detailed'}-product-list ${viewableClass} ${backgroundClass}`}>
        <ThemeOverride
          selector={`#${this.uid}`}
          textColor={this.props.data.primaryColor}
          backgroundColor={this.props.data.primaryColor}
          values={{ beforeBackground: helpers.hexToRGBA(this.props.data.primaryColor, 0.75) }}
        />
        <Header
          headerType={this.props.data.style}
          headerTitle={this.state.title}
          headerLink={{
            text: this.state.linkText,
            label: this.props.data.linkLabel,
            url: this.state.link,
          }}
          metaData={{
            name: this.state.listName,
            count: this.state.products.length,
          }}
        />
        { isHorizontal ? (
          <HorizontalScroller>
            { this.renderProducts() }
          </HorizontalScroller>
        ) : (
          <div className="product-list-content">
            { this.renderProducts() }
          </div>
        ) }
      </div>
    )
  }
}

ProductList.defaultProps = {
  data: {
    products: [],
    uid: '',
  },
  context: '',
  isDynamic: false,
}

ProductList.propTypes = {
  data: PropTypes.object,
  context: PropTypes.string,
  isDynamic: PropTypes.bool,
}

export default ProductList
